import React, { useState, useEffect } from 'react';
import { getConfig, saveConfig, isCapacitorApp } from '../config';
import { resetApiInstance, notificationsApi, NotificationPreference } from '../api';
import { CURRENT_APP_VERSION_NAME, getCurrentAppVersion } from '../appVersion';
import { checkLatestAppUpdate, isNativeAndroidApp, openAppDownload, openAppDownloadQr } from '../appUpdate';
import { initPushNotifications } from '../pushNotifications';

interface SettingsProps {
  onClose: () => void;
  onConfigChange: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onClose, onConfigChange }) => {
  const [apiUrl, setApiUrl] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'latest' | 'available' | 'error'>('idle');
  const [updateMessage, setUpdateMessage] = useState('');
  const [apkQrBusy, setApkQrBusy] = useState(false);
  const [appVersionName, setAppVersionName] = useState(CURRENT_APP_VERSION_NAME);

  // 消息通知偏好
  const [notifPref, setNotifPref] = useState<NotificationPreference | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMessage, setNotifMessage] = useState('');

  useEffect(() => {
    const config = getConfig();
    setApiUrl(config.apiBaseUrl);
    getCurrentAppVersion()
      .then((version) => setAppVersionName(version.versionName))
      .catch(() => setAppVersionName(CURRENT_APP_VERSION_NAME));
  }, []);

  useEffect(() => {
    setNotifLoading(true);
    notificationsApi.getPreference()
      .then((res) => setNotifPref(res.data))
      .catch(() => setNotifMessage('加载通知设置失败'))
      .finally(() => setNotifLoading(false));
  }, []);

  const patchPref = (patch: Partial<NotificationPreference>) => {
    setNotifPref((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleSaveNotif = async () => {
    if (!notifPref) return;
    setNotifSaving(true);
    setNotifMessage('');
    try {
      const res = await notificationsApi.updatePreference(notifPref);
      setNotifPref(res.data);
      setNotifMessage('已保存');
      // 若开启通知，确保设备已注册推送
      if (res.data.enabled) {
        initPushNotifications().catch(() => {});
      }
      setTimeout(() => setNotifMessage(''), 2000);
    } catch {
      setNotifMessage('保存失败，请重试');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleTestPush = async () => {
    setNotifMessage('正在发送测试推送...');
    try {
      await initPushNotifications();
      const res = await notificationsApi.testPush();
      const status = (res.data && (res.data as any).status) || '';
      if (status === 'sent') setNotifMessage('测试推送已发送，请查看通知栏');
      else if (status === 'skipped') setNotifMessage(`已跳过：${(res.data as any).reason || ''}（可能未注册设备或在免打扰时段）`);
      else setNotifMessage(`发送失败：${(res.data as any).reason || ''}`);
    } catch (e: any) {
      setNotifMessage('发送失败：' + (e?.message || '未知错误'));
    }
  };

  const handleSave = () => {
    saveConfig({ apiBaseUrl: apiUrl });
    resetApiInstance();
    onConfigChange();
    onClose();
  };

  const handleTest = async () => {
    setTestStatus('testing');
    setTestMessage('正在测试连接...');
    try {
      const response = await fetch(`${apiUrl}/api/people`, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (response.ok) { setTestStatus('success'); setTestMessage('连接成功！'); }
      else { setTestStatus('error'); setTestMessage(`连接失败: HTTP ${response.status}`); }
    } catch (error: any) { setTestStatus('error'); setTestMessage(`连接失败: ${error.message}`); }
  };

  const handleCheckUpdate = async () => {
    setUpdateStatus('checking');
    setUpdateMessage('正在检查更新...');
    try {
      const result = await checkLatestAppUpdate();
      setAppVersionName(result.current.versionName);
      if (result.hasUpdate && result.latest) {
        setUpdateStatus('available');
        setUpdateMessage(`发现新版本 ${result.latest.versionName}`);
        await openAppDownload(result.latest);
      } else {
        setUpdateStatus('latest');
        setUpdateMessage('当前已是最新版本');
      }
    } catch (error: any) {
      setUpdateStatus('error');
      setUpdateMessage(error?.message || '检查更新失败');
    }
  };

  const handleOpenApkQr = async () => {
    setApkQrBusy(true);
    setUpdateMessage('');
    try {
      await openAppDownloadQr();
    } catch (error: any) {
      setUpdateStatus('error');
      setUpdateMessage(error?.message || '打开 APK 扫码下载页失败');
    } finally {
      setApkQrBusy(false);
    }
  };

  const isApp = isCapacitorApp();
  const isLocalApiUrl = /:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(apiUrl);
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isWebLanAccess = !isApp && currentHost !== '' && !['localhost', '127.0.0.1', '::1'].includes(currentHost);
  const needsHostWarning = isLocalApiUrl && (isApp || isWebLanAccess);
  const suggestedApi = isWebLanAccess ? `${window.location.protocol}//${currentHost}:8000` : 'http://192.168.1.100:8000';

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="settings-header">
        <div className="settings-header-bg" />
        <div className="settings-header-inner">
          <button className="settings-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <h1>设置</h1>
          <div style={{ width: 36 }} />
        </div>
      </div>

      {/* Content */}
      <div className="settings-body">
        {/* Server Config */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">🌐</span>
            <h3>服务器配置</h3>
          </div>
          {needsHostWarning && (
            <div className="settings-localhost-warning">
              ⚠️ 当前访问环境下，<strong>localhost 指向当前设备本身</strong>，无法访问你电脑上的 Django。请改成可访问你电脑的地址，例如 <code>{suggestedApi}</code>（若为手机/模拟器，可在电脑终端执行 <code>ifconfig</code> 或 <code>ipconfig</code> 查看局域网 IP）。
            </div>
          )}
          <p className="settings-hint">
            请输入后端服务器地址。确保手机和电脑在同一 WiFi 下。
          </p>
          <div className="settings-field">
            <label>API 地址</label>
            <input type="text" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="http://192.168.1.100:8000" />
          </div>
          <button className="settings-test-btn" onClick={handleTest} disabled={testStatus === 'testing'}>
            {testStatus === 'testing' ? '测试中...' : '🔗 测试连接'}
          </button>
          {testMessage && (
            <div className={`settings-test-result ${testStatus}`}>
              {testStatus === 'success' && '✅ '}{testStatus === 'error' && '❌ '}{testMessage}
              {testStatus === 'error' && testMessage.includes('fetch') && needsHostWarning && (
                <div className="settings-result-hint">请将 localhost 改为可访问你电脑后端的地址后重试（例如 {suggestedApi}）。</div>
              )}
            </div>
          )}
        </div>

        {/* App Info */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">📱</span>
            <h3>应用信息</h3>
          </div>
          <div className="settings-info-row">
            <span>运行环境</span>
            <span className="settings-info-value">{isApp ? 'Native App' : 'Web'}</span>
          </div>
          <div className="settings-info-row">
            <span>平台</span>
            <span className="settings-info-value">Web</span>
          </div>
          <div className="settings-info-row" style={{ borderBottom: 'none' }}>
            <span>版本</span>
            <span className="settings-info-value">{appVersionName}</span>
          </div>
          <div className="settings-action-grid">
            {isNativeAndroidApp() && (
              <button className="settings-test-btn" onClick={handleCheckUpdate} disabled={updateStatus === 'checking'}>
                {updateStatus === 'checking' ? '检查中...' : '检查更新'}
              </button>
            )}
            <button className="settings-test-btn" onClick={handleOpenApkQr} disabled={apkQrBusy}>
              {apkQrBusy ? '打开中...' : '扫码下载 APK'}
            </button>
          </div>
          {updateMessage && (
            <div className={`settings-test-result ${updateStatus === 'error' ? 'error' : updateStatus === 'latest' ? 'success' : 'testing'}`}>
              {updateMessage}
            </div>
          )}
        </div>

        <div className="settings-card">
          <div className="settings-card-header"><h3>关于与隐私</h3></div>
          <nav aria-label="产品与法律信息" style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            <a href="/about.html" target="_blank" rel="noreferrer" style={{ color: '#c4b5fd' }}>产品介绍</a>
            <a href="/terms.html" target="_blank" rel="noreferrer" style={{ color: '#c4b5fd' }}>用户协议</a>
            <a href="/privacy.html" target="_blank" rel="noreferrer" style={{ color: '#c4b5fd' }}>隐私政策</a>
          </nav>
        </div>

        {/* Notifications */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">🔔</span>
            <h3>消息通知</h3>
          </div>
          {notifLoading && <p className="settings-hint">加载中...</p>}
          {!notifLoading && notifPref && (
            <>
              <div className="settings-switch-row">
                <span>开启通知</span>
                <label className="settings-switch">
                  <input type="checkbox" checked={notifPref.enabled}
                    onChange={(e) => patchPref({ enabled: e.target.checked })} />
                  <span className="settings-switch-slider" />
                </label>
              </div>

              <div className="settings-switch-row">
                <span>每日任务提醒</span>
                <label className="settings-switch">
                  <input type="checkbox" checked={notifPref.daily_task_enabled}
                    onChange={(e) => patchPref({ daily_task_enabled: e.target.checked })} />
                  <span className="settings-switch-slider" />
                </label>
              </div>
              {notifPref.daily_task_enabled && (
                <>
                  <div className="settings-field">
                    <label>任务提醒起始时间</label>
                    <input type="time" value={notifPref.daily_task_time}
                      onChange={(e) => patchPref({ daily_task_time: e.target.value })} />
                  </div>
                  <div className="settings-field">
                    <label>逐条间隔（分钟）</label>
                    <input type="number" min={5} max={1440} value={notifPref.daily_task_interval_minutes}
                      onChange={(e) => patchPref({ daily_task_interval_minutes: Number(e.target.value) })} />
                  </div>
                </>
              )}

              <div className="settings-switch-row">
                <span>饮食记录提醒</span>
                <label className="settings-switch">
                  <input type="checkbox" checked={notifPref.meal_reminder_enabled}
                    onChange={(e) => patchPref({ meal_reminder_enabled: e.target.checked })} />
                  <span className="settings-switch-slider" />
                </label>
              </div>
              {notifPref.meal_reminder_enabled && (
                <div className="settings-time-grid">
                  <div className="settings-field">
                    <label>早餐</label>
                    <input type="time" value={notifPref.breakfast_time}
                      onChange={(e) => patchPref({ breakfast_time: e.target.value })} />
                  </div>
                  <div className="settings-field">
                    <label>午餐</label>
                    <input type="time" value={notifPref.lunch_time}
                      onChange={(e) => patchPref({ lunch_time: e.target.value })} />
                  </div>
                  <div className="settings-field">
                    <label>晚餐</label>
                    <input type="time" value={notifPref.dinner_time}
                      onChange={(e) => patchPref({ dinner_time: e.target.value })} />
                  </div>
                </div>
              )}

              <div className="settings-switch-row">
                <span>免打扰时段</span>
                <label className="settings-switch">
                  <input type="checkbox" checked={notifPref.quiet_hours_enabled}
                    onChange={(e) => patchPref({ quiet_hours_enabled: e.target.checked })} />
                  <span className="settings-switch-slider" />
                </label>
              </div>
              {notifPref.quiet_hours_enabled && (
                <div className="settings-time-grid">
                  <div className="settings-field">
                    <label>开始</label>
                    <input type="time" value={notifPref.quiet_start}
                      onChange={(e) => patchPref({ quiet_start: e.target.value })} />
                  </div>
                  <div className="settings-field">
                    <label>结束</label>
                    <input type="time" value={notifPref.quiet_end}
                      onChange={(e) => patchPref({ quiet_end: e.target.value })} />
                  </div>
                </div>
              )}

              <button className="settings-test-btn" onClick={handleSaveNotif} disabled={notifSaving}>
                {notifSaving ? '保存中...' : '保存通知设置'}
              </button>
              <button className="settings-test-btn" style={{ marginTop: 8 }} onClick={handleTestPush}>
                发送测试推送
              </button>
              {notifMessage && (
                <div className="settings-test-result testing">{notifMessage}</div>
              )}
              {!isNativeAndroidApp() && (
                <p className="settings-hint" style={{ marginTop: 10, marginBottom: 0 }}>
                  提示：推送通知需在 Android App 内才能收到，Web 端仅可配置偏好。
                </p>
              )}
            </>
          )}
        </div>

        {/* Tips */}
        <div className="settings-card">
          <div className="settings-card-header">
            <span className="settings-card-icon">💡</span>
            <h3>使用提示</h3>
          </div>
          <ul className="settings-tips">
            <li>确保后端服务器正在运行</li>
            <li>使用电脑的局域网 IP 地址而非 localhost</li>
            <li>检查防火墙是否允许 8000 端口访问</li>
            <li>Android 设备需要允许明文 HTTP 流量</li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <div className="settings-footer">
        <button className="settings-save-btn" onClick={handleSave}>保存设置</button>
      </div>

      <style>{`
        .settings-page {
          min-height: 100vh; background: var(--bg);
          display: flex; flex-direction: column;
        }
        .settings-header {
          position: relative; overflow: hidden;
        }
        .settings-header-bg {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, #6C5CE7 0%, #A29BFE 50%, #74B9FF 100%);
        }
        .settings-header-bg::after {
          content: ''; position: absolute; bottom: -20px; left: -10%; right: -10%;
          height: 50px; background: var(--bg); border-radius: 50% 50% 0 0;
        }
        .settings-header-inner {
          position: relative; z-index: 1;
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px;
          padding-top: calc(16px + var(--status-bar-height, 28px));
          padding-bottom: 28px;
        }
        .settings-header-inner h1 {
          margin: 0; font-size: 20px; font-weight: 700; color: white;
        }
        .settings-back {
          width: 36px; height: 36px; border-radius: var(--radius-full);
          background: rgba(255,255,255,0.2); border: none; color: white;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          transition: all 200ms;
        }
        .settings-back:active { transform: scale(0.9); background: rgba(255,255,255,0.3); }

        .settings-body {
          flex: 1; padding: 0 20px 20px; margin-top: -8px;
        }
        .settings-card {
          background: var(--surface); border-radius: var(--radius-lg);
          padding: 20px; margin-bottom: 14px;
          box-shadow: var(--shadow-sm); border: 1px solid var(--border-light);
        }
        .settings-card-header {
          display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
        }
        .settings-card-icon { font-size: 20px; }
        .settings-card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: var(--text-primary); }
        .settings-hint {
          font-size: 13px; color: var(--text-tertiary); line-height: 1.5; margin-bottom: 16px;
        }
        .settings-localhost-warning {
          margin-bottom: 14px; padding: 12px; background: #FFF8E6; border: 1px solid #E6C200;
          border-radius: var(--radius-md); font-size: 13px; line-height: 1.6; color: #5c4a00;
        }
        .settings-localhost-warning code { background: rgba(0,0,0,0.08); padding: 2px 6px; border-radius: 4px; font-size: 12px; }
        .settings-result-hint { margin-top: 8px; font-size: 12px; opacity: 0.9; }
        .settings-field { margin-bottom: 14px; }
        .settings-field label {
          display: block; font-size: 11px; font-weight: 600; color: var(--text-tertiary);
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;
        }
        .settings-field input {
          width: 100%; padding: 12px 14px; border: 1.5px solid var(--border);
          border-radius: var(--radius-md); font-size: 15px; font-family: inherit;
          color: var(--text-primary); background: var(--bg);
          transition: border-color 150ms; box-sizing: border-box;
        }
        .settings-field input:focus { outline: none; border-color: var(--primary); background: var(--surface); }
        .settings-test-btn {
          width: 100%; padding: 12px; border: 1.5px solid var(--border);
          border-radius: var(--radius-md); background: var(--bg);
          font-size: 14px; font-weight: 500; font-family: inherit;
          color: var(--text-primary); cursor: pointer; transition: all 200ms;
        }
        .settings-test-btn:active { background: var(--border); transform: scale(0.98); }
        .settings-test-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .settings-action-grid {
          display: grid; gap: 10px; margin-top: 12px;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        }
        .settings-test-result {
          margin-top: 12px; padding: 12px; border-radius: var(--radius-sm); font-size: 14px;
        }
        .settings-test-result.success { background: var(--success-bg); color: var(--success); }
        .settings-test-result.error { background: var(--danger-bg); color: var(--danger); }
        .settings-test-result.testing { background: var(--primary-bg); color: var(--primary); }

        .settings-info-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0; border-bottom: 1px solid var(--border-light);
          font-size: 14px; color: var(--text-secondary);
        }
        .settings-info-value { font-weight: 600; color: var(--text-primary); }

        .settings-switch-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 12px 0; border-bottom: 1px solid var(--border-light);
          font-size: 14px; color: var(--text-secondary);
        }
        .settings-switch {
          position: relative; display: inline-block; width: 46px; height: 26px; flex-shrink: 0;
        }
        .settings-switch input { opacity: 0; width: 0; height: 0; }
        .settings-switch-slider {
          position: absolute; cursor: pointer; inset: 0;
          background: var(--border); border-radius: var(--radius-full);
          transition: 200ms;
        }
        .settings-switch-slider::before {
          content: ''; position: absolute; height: 20px; width: 20px; left: 3px; bottom: 3px;
          background: white; border-radius: 50%; transition: 200ms;
        }
        .settings-switch input:checked + .settings-switch-slider {
          background: linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%);
        }
        .settings-switch input:checked + .settings-switch-slider::before {
          transform: translateX(20px);
        }
        .settings-time-grid {
          display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap;
        }
        .settings-time-grid .settings-field { flex: 1; min-width: 90px; margin-bottom: 0; }
        .settings-field input[type="time"], .settings-field input[type="number"] {
          width: 100%; padding: 12px 14px; border: 1.5px solid var(--border);
          border-radius: var(--radius-md); font-size: 15px; font-family: inherit;
          color: var(--text-primary); background: var(--bg); box-sizing: border-box;
        }

        .settings-tips {
          margin: 0; padding-left: 18px; color: var(--text-secondary);
          font-size: 13px; line-height: 2;
        }

        .settings-footer {
          padding: 16px 20px;
          padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
          background: var(--surface); border-top: 1px solid var(--border-light);
        }
        .settings-save-btn {
          width: 100%; padding: 14px; border: none;
          border-radius: var(--radius-lg);
          background: linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%);
          color: white; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: var(--shadow-primary);
          transition: all 200ms;
        }
        .settings-save-btn:active { transform: scale(0.98); opacity: 0.9; }

        @media (min-width: 768px) {
          .settings-body { max-width: 560px; margin-left: auto; margin-right: auto; }
        }
      `}</style>
    </div>
  );
};

export default Settings;
